<template>
  <div>
    <b-card no-body>
      <b-card-body>
        <b-row>
          <b-col cols="12" md="3" class="mb-1">
            <label>Статус</label>
            <b-form-select v-model="statusFilter" :options="statusOptions" />
          </b-col>
          <b-col cols="12" md="3" class="mb-1">
            <label>Тип</label>
            <b-form-select v-model="typeFilter" :options="typeOptions" />
          </b-col>
          <b-col cols="12" md="3" class="mb-1">
            <label>Автор</label>
            <b-form-select v-model="authorFilter" :options="authorOptions" />
          </b-col>
          <b-col cols="12" md="3" class="mb-1 d-flex align-items-end justify-content-md-end">
            <h5 class="mb-50">
              На одобрении:
              <b-badge variant="warning">{{ pendingCount }}</b-badge>
            </h5>
          </b-col>
        </b-row>
      </b-card-body>
    </b-card>

    <b-overlay :show="isLoading" rounded="sm">
      <b-card no-body>
        <b-card-body>
          <vue-good-table
            :rows="rows"
            :columns="columns"
            :sort-options="{ enabled: false }"
            :pagination-options="{ enabled: false }"
          >
            <template slot="emptystate">
              <div class="text-center text-muted py-2">Заявок нет</div>
            </template>
            <template slot="table-row" slot-scope="props">
              <div v-if="props.column.field === 'submission'">
                <submission-preview :submission="props.row" />
                <small v-if="props.row.status === 'pending' && props.row.depends_on_id" class="text-muted d-block mt-50">
                  Ждёт заявку #{{ props.row.depends_on_id }}
                </small>
              </div>
              <span v-else-if="props.column.field === 'artist'">
                <b-link
                  v-if="props.row.artist"
                  :to="{ name: 'artist-view', params: { id: props.row.artist.id } }"
                >
                  {{ props.row.artist.title }}
                </b-link>
                <span v-else>—</span>
              </span>
              <span v-else-if="props.column.field === 'author'">
                {{ props.row.author ? props.row.author.name : '—' }}
              </span>
              <span v-else-if="props.column.field === 'created_at'">
                {{ formatDateTime(props.row.created_at) }}
              </span>
              <span v-else-if="props.column.field === 'amount'">
                <span v-if="props.row.status === 'approved'">{{ formatRub(props.row.amount) }}</span>
                <span v-else>—</span>
              </span>
              <div v-else-if="props.column.field === 'decision'">
                <div v-if="props.row.status === 'pending'" style="min-width: 220px">
                  <b-form-input
                    v-model="comments[props.row.id]"
                    size="sm"
                    placeholder="Комментарий"
                    class="mb-50"
                    :disabled="busyId === props.row.id"
                  />
                  <div class="d-flex">
                    <b-button
                      variant="success"
                      size="sm"
                      class="mr-50"
                      :disabled="busyId === props.row.id"
                      @click="decide(props.row, 'approve')"
                    >
                      Одобрить
                    </b-button>
                    <b-button
                      variant="outline-danger"
                      size="sm"
                      :disabled="busyId === props.row.id || !(comments[props.row.id] || '').trim()"
                      @click="decide(props.row, 'reject')"
                    >
                      Отклонить
                    </b-button>
                  </div>
                </div>
                <div v-else>
                  <b-badge :variant="statusVariants[props.row.status] || 'light-secondary'">
                    {{ props.row.status_label }}
                  </b-badge>
                  <b-badge v-if="props.row.is_paid" variant="success" class="ml-50">Оплачено</b-badge>
                  <small v-if="props.row.reviewer" class="d-block text-muted mt-50">
                    {{ props.row.reviewer.name }}
                    <span v-if="props.row.reviewed_at">, {{ formatDateTime(props.row.reviewed_at) }}</span>
                  </small>
                  <small v-if="props.row.review_comment" class="d-block">{{ props.row.review_comment }}</small>
                </div>
              </div>
              <span v-else>{{ props.formattedRow[props.column.field] }}</span>
            </template>
          </vue-good-table>

          <div v-if="total > perPage" class="d-flex justify-content-center mt-2">
            <b-pagination
              v-model="currentPage"
              :total-rows="total"
              :per-page="perPage"
              class="mb-0"
            />
          </div>
        </b-card-body>
      </b-card>
    </b-overlay>
  </div>
</template>

<script>
import {
  BCard, BCardBody, BRow, BCol, BButton, BOverlay, BLink, BBadge, BFormSelect, BFormInput, BPagination,
} from 'bootstrap-vue'
import { VueGoodTable } from 'vue-good-table'
import { ref, watch } from '@vue/composition-api'
import store from '@/store'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { formatDateTime } from '@core/utils/filter'
import { formatRub } from '@/views/finance/utils'
import {
  SUBMISSION_STATUS_VARIANTS, SUBMISSION_TYPE_OPTIONS, SUBMISSION_STATUS_OPTIONS,
} from '@/libs/moderation'
import SubmissionPreview from './SubmissionPreview.vue'

export default {
  components: {
    BCard,
    BCardBody,
    BRow,
    BCol,
    BButton,
    BOverlay,
    BLink,
    BBadge,
    BFormSelect,
    BFormInput,
    BPagination,
    VueGoodTable,
    SubmissionPreview,
  },
  setup() {
    const toast = useToast()
    const showError = title => toast({
      component: ToastificationContent,
      props: { title, icon: 'AlertTriangleIcon', variant: 'danger' },
    })
    const showSuccess = title => toast({
      component: ToastificationContent,
      props: { title, icon: 'CheckIcon', variant: 'success' },
    })

    const isLoading = ref(false)
    const rows = ref([])
    const total = ref(0)
    const perPage = ref(15)
    const currentPage = ref(1)
    const pendingCount = ref(0)
    const busyId = ref(null)
    const comments = ref({})

    const statusFilter = ref('pending')
    const typeFilter = ref('')
    const authorFilter = ref(null)
    const authorOptions = ref([{ value: null, text: 'Все авторы' }])

    const statusOptions = SUBMISSION_STATUS_OPTIONS
    const typeOptions = SUBMISSION_TYPE_OPTIONS
    const statusVariants = SUBMISSION_STATUS_VARIANTS

    const columns = [
      { label: 'Заявка', field: 'submission' },
      { label: 'Артист', field: 'artist' },
      { label: 'Автор', field: 'author' },
      { label: 'Создана', field: 'created_at' },
      { label: 'Сумма', field: 'amount' },
      { label: 'Решение', field: 'decision' },
    ]

    const errorMessage = err => {
      const data = (err.response && err.response.data) || {}
      if (data.errors) return Object.values(data.errors)[0][0]
      return data.message || 'Ошибка'
    }

    const fetchPendingCount = () => {
      store.dispatch('moderation/fetchPendingCount')
        .then(response => { pendingCount.value = response.data.count || 0 })
        .catch(err => showError(errorMessage(err)))
    }

    const fetchAuthors = () => {
      store.dispatch('moderation/fetchAuthors')
        .then(response => {
          const authors = Array.isArray(response.data) ? response.data : (response.data.data || [])
          authorOptions.value = [
            { value: null, text: 'Все авторы' },
            ...authors.map(author => ({ value: author.id, text: author.name })),
          ]
        })
        .catch(err => showError(errorMessage(err)))
    }

    const fetchSubmissions = () => {
      isLoading.value = true
      const params = { status: statusFilter.value, page: currentPage.value }
      if (typeFilter.value) params.type = typeFilter.value
      if (authorFilter.value) params.author_id = authorFilter.value
      store.dispatch('moderation/fetchSubmissions', params)
        .then(response => {
          const body = response.data || {}
          rows.value = body.data || []
          const meta = body.meta || {}
          total.value = meta.total || 0
          perPage.value = meta.per_page || 15
          // Страница опустела (последнюю заявку на ней рассмотрели) — уходим на последнюю непустую;
          // смена currentPage сама перезагрузит список через watch
          if (rows.value.length === 0 && currentPage.value > 1) {
            currentPage.value = Math.max(1, Math.min(currentPage.value - 1, meta.last_page || 1))
          }
        })
        .catch(err => showError(errorMessage(err)))
        .finally(() => { isLoading.value = false })
    }

    // Смена фильтра — возврат на первую страницу; если страница уже первая, грузим сами
    watch([statusFilter, typeFilter, authorFilter], () => {
      if (currentPage.value === 1) fetchSubmissions()
      else currentPage.value = 1
    })
    watch(currentPage, fetchSubmissions)

    const decide = (row, action) => {
      const comment = (comments.value[row.id] || '').trim()
      if (action === 'reject' && !comment) return
      busyId.value = row.id
      store.dispatch(`moderation/${action}`, { id: row.id, comment: comment || null })
        .then(() => {
          showSuccess(action === 'approve' ? 'Одобрено' : 'Отклонено')
          const updatedComments = { ...comments.value }
          delete updatedComments[row.id]
          comments.value = updatedComments
          // Перезагружаем с сервера: отклонение каскадно закрывает зависимые заявки, и total/счётчик меняются не на 1
          fetchSubmissions()
          fetchPendingCount()
        })
        .catch(err => showError(errorMessage(err)))
        .finally(() => { busyId.value = null })
    }

    fetchPendingCount()
    fetchAuthors()
    fetchSubmissions()

    return {
      isLoading,
      rows,
      total,
      perPage,
      currentPage,
      pendingCount,
      busyId,
      comments,
      statusFilter,
      typeFilter,
      authorFilter,
      authorOptions,
      statusOptions,
      typeOptions,
      statusVariants,
      columns,
      decide,
      formatDateTime,
      formatRub,
    }
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-good-table.scss';
</style>
